import React, {useMemo} from 'react';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {t} from '../../../../../../services/i18n';
import {dbDateFormat, formatPriceWithCurrencyLTR, sortCourseEventDays, daysEnum} from '../../../../../../services/Helpers';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import HStack from '../../../../../common/Layout/HStack';
import AppText from '../../../../../common/AppText';

const COL_WIDTHS_WITH_LOCATION = {
    name: '215px',
    price: '120px',
    date: '105px',
    days: '85px',
    location: '94px',
};

const wrapColumnStyle = (width) => ({
    width,
    flexShrink: 0,
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    alignSelf: 'center'
});

const HugCollapseLabel = ({ hug, showLocation }) => {
    const box = useSelector((state) => state.box.box);
    const dateFormat = useMemo(() => (box ? box.locations_box[0].date_format : null), [box]);
    const currencySymbol = useMemo(() => box?.locations_box?.[0]?.currency_symbol ?? '₪', [box]);

    const formatDateRange = () => {
        if (!hug.start_date) return null;
        const start = moment(hug.start_date, dbDateFormat).format(dateFormat ?? 'DD/MM/YY');
        if (!hug.end_date) return t('start-on', { start });
        const end = moment(hug.end_date, dbDateFormat).format(dateFormat ?? 'DD/MM/YY');
        return `${start} - ${end}`;
    };

    const formatBasePrice = () => {
        const recurringMt = hug.membership_types_all?.find(mt => mt.type === 'recurring');
        const regularMt = hug.membership_types_all?.find(mt => mt.type === 'regular');

        const parts = [];
        if (recurringMt) {
            parts.push(t('price-recurring-payment', { price: formatPriceWithCurrencyLTR(recurringMt.price, currencySymbol) }));
        }
        if (regularMt) {
            parts.push(t('price-regular-payment', { price: formatPriceWithCurrencyLTR(regularMt.price, currencySymbol) }));
        }

        if (!parts.length) return null;
        return parts.join(' ');
    };

    const formatDays = () => {
        const isOneDayOfWeek = hug.series_events.length === 1
        let days = sortCourseEventDays(hug.series_events).map(day => {
            if (isOneDayOfWeek) return t(`day-${day.day_of_week + 1}`)
            const dayName = daysEnum[day.day_of_week]
            return t(`${dayName}Shortened`)
        })
        return isOneDayOfWeek ? t('in-day', {day: days[0]}) : t('in-days', {days: days.join(', ')})
    }

    const widths = COL_WIDTHS_WITH_LOCATION;

    return (
        <HStack alignItems={'flex-start'} flex={1} space={'1.5rem'} style={{ minWidth: 0 }}>
            <AppText
                fontSize={GlobalStyleAttributes.fonts.heading3}
                boldFont
                style={wrapColumnStyle(widths.name)}
            >
                {hug.series_name}
            </AppText>
            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={wrapColumnStyle(widths.price)}>
                {formatBasePrice() ?? '—'}
            </AppText>
            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={wrapColumnStyle(widths.date)}>
                {formatDateRange() ?? '—'}
            </AppText>
            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={wrapColumnStyle(widths.days)}>
                {formatDays() ?? '—'}
            </AppText>
            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} style={wrapColumnStyle(widths.location)}>
                {showLocation &&(hug.locations_box?.location ?? '—')}
            </AppText>
        </HStack>
    );
};

export default HugCollapseLabel;
